/**
 * Shared enrichment-eligibility utilities for the Contacts module.
 *
 * Extracted so that:
 *  1. The opt-out check is a single source of truth (ADR 001.1 — DRY).
 *  2. Pure functions can be unit-tested without mounting a React component
 *     (ADR 001.2 — Modular).
 *
 * Bug fixed here: the `doNotBulkEnrich` flag is stored under
 * `contact.attributes.doNotBulkEnrich` on the detail endpoint but is
 * **also flattened to `contact.doNotBulkEnrich`** on the list endpoint.
 * Previous guards only checked one shape, causing the opt-out to be ignored
 * when contacts were opened from the list view (issue #2388).
 */

import type { ContactObject } from './contactTypes';

/** Minimal contact shape required by the enrichment utilities. */
export interface EnrichmentContact {
  name?: string;
  title?: string;
  summary?: string;
  enrichmentSummary?: string;
  enrichmentConfidence?: string;
  enrichmentAttemptedAt?: string;
  enrichmentReviewedAt?: string;
  linkedinUrl?: string;
  location?: string;
  company?: string;
  organization?: string;
  /** Top-level opt-out flag (list-endpoint shape). */
  doNotBulkEnrich?: boolean;
  attributes?: Record<string, unknown>;
}

/**
 * Returns `true` when an enrichment attempt has already produced a positive
 * result (confidence = high | medium | low).  These contacts have an
 * `enrichmentSummary` by construction, so this is a belt-and-suspenders
 * guard for the rare case where the summary field is missing from the cached
 * object (stale cache, partial API response, etc.).
 */
export function isAlreadyEnriched(contact: EnrichmentContact): boolean {
  const meta = getEnrichmentMeta(contact);
  if (meta.summary) return true; // primary signal: enrichmentSummary present
  const conf = (contact.enrichmentConfidence || (contact.attributes as Record<string, unknown> | undefined)?.enrichmentConfidence || '').toString().toLowerCase();
  return conf === 'high' || conf === 'medium' || conf === 'low';
}

/**
 * Returns `true` when a contact has opted out of automatic enrichment.
 *
 * Checks **both** data shapes:
 *   - `contact.doNotBulkEnrich`         — list-endpoint (API flattens attributes)
 *   - `contact.attributes.doNotBulkEnrich` — detail-endpoint (nested)
 *
 * This dual-shape check is the single source of truth for the opt-out guard.
 * All auto-enrich entry points must use this function instead of rolling their
 * own attribute-path check.
 *
 * Accepts any object with the relevant fields to support multiple contact types
 * (ContactObject from ContactEditModal, ContactObject from ContactListModule, etc.).
 * This flexibility is intentional to reduce coupling across the contacts module
 * while maintaining type safety at call sites.
 */
export function isAutoEnrichOptedOut(contact: unknown): boolean {
  if (!contact || typeof contact !== 'object') return false;
  const c = contact as Record<string, unknown>;
  // Check top-level flag (list-endpoint shape)
  if (c.doNotBulkEnrich === true) return true;
  // Check nested shape (detail-endpoint shape)
  const attrs = c.attributes as Record<string, unknown> | undefined;
  return attrs?.doNotBulkEnrich === true;
}

export type EnrichmentMeta = {
  summary?: string;
  confidence?: string;
  attemptedAt?: string;
  reviewedAt?: string;
};

/** Extract enrichment metadata, checking both top-level and nested shapes. */
export function getEnrichmentMeta(contact: EnrichmentContact): EnrichmentMeta {
  const attrs = (contact.attributes ?? {}) as Record<string, any>;
  return {
    summary: contact.enrichmentSummary || attrs.enrichmentSummary || '',
    confidence: contact.enrichmentConfidence || attrs.enrichmentConfidence || '',
    attemptedAt: contact.enrichmentAttemptedAt || attrs.enrichmentAttemptedAt || '',
    reviewedAt: contact.enrichmentReviewedAt || attrs.enrichmentReviewedAt || '',
  };
}

/**
 * Returns `true` when a contact should be included in an automatic enrichment
 * run (bulk or post-create).
 *
 * A contact is eligible when ALL of the following hold:
 *  - Has a non-empty name to search
 *  - Has NOT opted out of auto-enrichment (`isAutoEnrichOptedOut`)
 *  - Has not already been enriched (enrichmentSummary present OR confidence is
 *    high/medium/low — belt-and-suspenders against stale cache missing the summary)
 *  - Has not been manually reviewed/skipped by the user
 *  - Is still missing at least one of: summary, company, LinkedIn URL, location
 *
 * The manual "Build AI profile" button is NOT gated by this function — it
 * always fires because it is an explicit user action.
 */
export function needsEnrichment(contact: EnrichmentContact): boolean {
  const name = (contact.name || contact.title || '').trim();
  if (!name) return false;
  // Explicit opt-outs win over all other checks.
  if (isAutoEnrichOptedOut(contact)) return false;
  // Belt-and-suspenders: exclude contacts where enrichment already produced a
  // result (summary present) OR where confidence indicates a completed attempt
  // (high/medium/low).  This guards against the stale-cache case where the
  // query cache still has the pre-enrichment object but enrichmentConfidence
  // has been updated.  Contacts with confidence='none' remain eligible because
  // the API found nothing and a retry may succeed with more data.
  if (isAlreadyEnriched(contact)) return false;
  const meta = getEnrichmentMeta(contact);
  if (meta.reviewedAt) return false; // user marked as reviewed/skipped
  // Has any of the enrichable fields blank? Keep predicate loose: a contact
  // with only a name + email is the prime target.
  const company = contact.company || contact.organization || '';
  const attrs = contact.attributes as Record<string, any> | undefined;
  const linkedin = contact.linkedinUrl || attrs?.linkedinUrl || '';
  const location = contact.location || attrs?.location || '';
  return !contact.summary || !company || !linkedin || !location;
}

const GENERIC_EMAIL_DOMAINS = new Set([
  'gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com',
  'me.com','mail.com','protonmail.com','proton.me','live.com','msn.com',
  'ymail.com','comcast.net','att.net','verizon.net','sbcglobal.net','cox.net','charter.net',
]);
export function companyFromEmailDomain(email: string | undefined): string {
  if (!email) return '';
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain || GENERIC_EMAIL_DOMAINS.has(domain)) return '';
  const raw = domain.split('.')[0].replace(/[-_]/g, ' ');
  return raw.replace(/\b\w/g, c => c.toUpperCase());
}

// Build enrichment request payload from whatever we already know about a
// contact. The endpoint accepts `hints` (folded into the search query) and
// `knownData` (treated as ground truth and merged into the resulting
// profile). Sending the email + domain-derived company dramatically
// improves disambiguation for low-presence professionals — without it,
// the search fires on the bare name and frequently returns multiple
// candidates → 'low' confidence.
export function buildEnrichmentRequest(contact: ContactObject): { name: string; hints?: string; knownData?: Record<string, string> } {
  const name = (contact.name || contact.title || '').trim();
  const email = contact.email || contact.emails?.[0] || '';
  const company = (contact.company || contact.organization || '').trim();
  const inferredCompany = company || companyFromEmailDomain(email);
  const phone = contact.phone || contact.phones?.[0] || '';
  const attrs = (contact.attributes ?? {}) as Record<string, any>;
  const linkedin = contact.linkedinUrl || attrs.linkedinUrl || '';
  const location = contact.location || attrs.location || '';
  const contactTitle = (attrs.jobTitle as string | undefined) || (attrs.contactTitle as string | undefined) || '';
  // Hints fold into the search query — biased toward disambiguation. The
  // email is the single highest-signal disambiguator we have for common
  // names ("evan rifkin" alone matches dozens of people; their email +
  // name almost never does). Include it whenever present so bulk runs
  // get the same disambiguation as a manual click that knew the email
  // upfront. Also fold in title, status, and tags (#1438) — status like
  // "FUNDED" or tags like "Bitcoin investor" are high-signal context
  // clues that disambiguate namesakes (e.g., a high school football
  // player vs a crypto investor both named "Matteo Pellegrini").
  const hintParts = [
    email,
    contactTitle,
    inferredCompany,
    location,
    contact.status,
    contact.tags?.join(', '),
  ].filter(Boolean);
  const hints = hintParts.join(', ') || undefined;
  // knownData is incorporated as facts, not searched. Only send fields
  // that have a value so the prompt stays clean.
  const knownData: Record<string, string> = {};
  if (email) knownData.email = email;
  if (contactTitle) knownData.title = contactTitle;
  if (inferredCompany) knownData.company = inferredCompany;
  if (phone) knownData.phone = phone;
  if (linkedin) knownData.linkedinUrl = linkedin;
  if (location) knownData.location = location;
  return { name, hints, knownData: Object.keys(knownData).length ? knownData : undefined };
}
